// @ts-nocheck
// Note: react-konva types have conflicts, using nocheck
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Circle, Line, Text, Image as KonvaImage, Group, Transformer, Ellipse, Path } from 'react-konva';
import styled from 'styled-components';
import Konva from 'konva';
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  CANVAS_BACKGROUND,
  SchemaElement,
  EditorState,
  EditorTool,
  DEFAULT_EDITOR_STATE,
  MIN_ZOOM,
  MAX_ZOOM,
  NodeWithPosition,
  EditorMode,
  IconType,
} from '../../types/mapEditor';
import EditorToolbar from './EditorToolbar';
import TemplateLibrary from './TemplateLibrary';
import NodePositionManager from './NodePositionManager';
import DevicesPanel from './DevicesPanel';
import { getDeviceColor } from './DeviceIcons';
import { OperationTemplate, OperationTemplateRequest, TemplateDevicePosition } from '../../types/mapEditor';
import { getOperationTemplates, createOperationTemplate, updateOperationTemplate, deleteOperationTemplate } from '../../services/mapEditorService';

const EditorContainer = styled.div<{ $isFullscreen: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${props => props.$isFullscreen ? '100vh' : 'calc(100vh - 100px)'};
  background: #0a0a15;
  position: ${props => props.$isFullscreen ? 'fixed' : 'relative'};
  top: ${props => props.$isFullscreen ? '0' : 'auto'};
  left: ${props => props.$isFullscreen ? '0' : 'auto'};
  z-index: ${props => props.$isFullscreen ? '9999' : 'auto'};
`;

const PlacementHint = styled.div`
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(76, 175, 80, 0.9);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 100;
  pointer-events: none;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const CanvasContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: #0a0a15;
`;

const StageWrapper = styled.div`
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  cursor: ${props => props.$cursor || 'default'};
`;

const TextInputOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const TextInputDialog = styled.div`
  background: #1a1a2e;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid #333;

  h3 {
    color: #fff;
    margin: 0 0 16px 0;
  }

  input {
    width: 300px;
    padding: 12px;
    background: #0a0a15;
    border: 1px solid #444;
    border-radius: 4px;
    color: #fff;
    font-size: 16px;
    margin-bottom: 16px;

    &:focus {
      outline: none;
      border-color: #4CAF50;
    }
  }

  .buttons {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  button {
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;

    &.primary {
      background: #4CAF50;
      border: none;
      color: #fff;
    }

    &.secondary {
      background: transparent;
      border: 1px solid #444;
      color: #fff;
    }
  }
`;

interface MapSchemaEditorProps {
  mapId: string;
  mapData: {
    id: string;
    name: string;
    fieldId?: string;
    schemaImageUrl?: string;
    schemaElements?: SchemaElement[];
  };
  nodes?: NodeWithPosition[];
  mode: EditorMode;
  onSave?: (elements: SchemaElement[], canvasBgColor: 'dark' | 'white', imageDataUrl: string) => void;
  onNodePositionUpdate?: (nodeId: string, x: number, y: number, scale: number, locked: boolean) => void;
  onExportImage?: (dataUrl: string) => void;
  onShowSuccess?: (title: string, message: string, duration?: number) => void;
  isReadOnly?: boolean;
}

const MapSchemaEditor: React.FC<MapSchemaEditorProps> = ({
  mapId,
  mapData,
  nodes = [],
  mode,
  onSave,
  onNodePositionUpdate,
  onExportImage,
  onShowSuccess,
  isReadOnly = false,
}) => {

  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const transformerRef = useRef<Konva.Transformer>(null);

  const [editorState, setEditorState] = useState<EditorState>(DEFAULT_EDITOR_STATE);
  const [elements, setElements] = useState<SchemaElement[]>(mapData.schemaElements || []);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [stageSize, setStageSize] = useState({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentElement, setCurrentElement] = useState<SchemaElement | null>(null);
  const [history, setHistory] = useState<{ past: SchemaElement[][]; future: SchemaElement[][] }>({
    past: [],
    future: [],
  });

  // Polygon drawing state
  const [polygonPoints, setPolygonPoints] = useState<number[]>([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);

  // Text input state
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputPosition, setTextInputPosition] = useState({ x: 0, y: 0 });
  const [textInputValue, setTextInputValue] = useState('');
  const [editingTextId, setEditingTextId] = useState<string | null>(null);

  // Template library state
  const [isLibraryCollapsed, setIsLibraryCollapsed] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Partial<SchemaElement> | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Canvas background color (white or dark)
  const [canvasBgColor, setCanvasBgColor] = useState<'dark' | 'white'>(mapData.canvasBgColor || 'dark');

  // Node position manager state
  const [isNodeManagerCollapsed, setIsNodeManagerCollapsed] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodeToPlace, setNodeToPlace] = useState<NodeWithPosition | null>(null);

  // Devices panel state
  const [operationTemplates, setOperationTemplates] = useState<OperationTemplate[]>([]);
  const [selectedOperationTemplateId, setSelectedOperationTemplateId] = useState<string | null>(null);
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState<number | null>(null);
  const [selectedDeviceForPlacement, setSelectedDeviceForPlacement] = useState<TemplateDevicePosition | null>(null);
  const [devicePositions, setDevicePositions] = useState<TemplateDevicePosition[]>([]);
  const [isDevicesPanelCollapsed, setIsDevicesPanelCollapsed] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [deviceIconImages, setDeviceIconImages] = useState<Record<string, HTMLImageElement>>({});

  // Pan and zoom state
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPosition, setLastPanPosition] = useState({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);

  // Multi-select drag state (using refs for synchronous access during drag)
  const dragStartPositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const isDraggingMultipleRef = useRef(false);

  // Load background image
  useEffect(() => {
    if (mapData.schemaImageUrl) {
      const img = new window.Image();
      // Note: crossOrigin removed to avoid CORS issues
      // Export to image won't work, but display will
      img.src = mapData.schemaImageUrl;
      img.onload = () => {
        setBackgroundImage(img);
      };
      img.onerror = (err) => {
        console.error('Failed to load background image:', err);
        // Try without any special settings
      };
    }
  }, [mapData.schemaImageUrl]);

  // Load operation templates when mode changes to devices
  useEffect(() => {
    if (mode === 'devices' && mapId) {
      const loadTemplates = async () => {
        setLoadingTemplates(true);
        try {
          const templates = await getOperationTemplates(mapId);
          setOperationTemplates(templates);
        } catch (err) {
          console.error('Failed to load operation templates:', err);
        } finally {
          setLoadingTemplates(false);
        }
      };
      loadTemplates();
    }
  }, [mode, mapId]);

  // Load device icon images
  useEffect(() => {
    devicePositions.forEach(device => {
      if (device.iconUrl && !deviceIconImages[device.fmdId]) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.src = device.iconUrl;
        img.onload = () => {
          setDeviceIconImages(prev => ({ ...prev, [device.fmdId]: img }));
        };
        img.onerror = () => {
          console.warn(`Failed to load icon for device ${device.fmdId}`);
        };
      }
    });
  }, [devicePositions]);

  // Track if initial setup has been done
  const [isInitialized, setIsInitialized] = useState(false);

  // Calculate zoom to fit canvas in container
  const calculateFitZoom = useCallback(() => {
    if (!containerRef.current) return 1;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const padding = 40;
    const availableWidth = containerWidth - padding;
    const availableHeight = containerHeight - padding;
    const fitZoom = Math.min(
      availableWidth / CANVAS_WIDTH,
      availableHeight / CANVAS_HEIGHT,
      1 // Don't zoom in more than 100%
    );
    return Math.max(MIN_ZOOM, fitZoom);
  }, []);

  // Center canvas at current zoom level
  const centerCanvas = useCallback((zoom: number = editorState.zoom) => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      // Center the canvas within the container
      const canvasScaledWidth = CANVAS_WIDTH * zoom;
      const canvasScaledHeight = CANVAS_HEIGHT * zoom;
      setStagePosition({
        x: (containerWidth - canvasScaledWidth) / 2,
        y: (containerHeight - canvasScaledHeight) / 2,
      });
    }
  }, [editorState.zoom]);

  // Reset view - fit canvas to container and center
  const handleResetView = useCallback(() => {
    if (!containerRef.current) return;
    const fitZoom = calculateFitZoom();
    setEditorState(prev => ({ ...prev, zoom: fitZoom }));
    centerCanvas(fitZoom);
  }, [calculateFitZoom, centerCanvas]);

  // Calculate canvas scale to fit container
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        setStageSize({
          width: containerWidth,
          height: containerHeight,
        });

        // Only set initial zoom on first mount
        if (!isInitialized) {
          const fitZoom = calculateFitZoom();
          setEditorState(prev => ({ ...prev, zoom: fitZoom }));
          const canvasScaledWidth = CANVAS_WIDTH * fitZoom;
          const canvasScaledHeight = CANVAS_HEIGHT * fitZoom;
          setStagePosition({
            x: (containerWidth - canvasScaledWidth) / 2,
            y: (containerHeight - canvasScaledHeight) / 2,
          });
          setIsInitialized(true);
        }
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [isInitialized, calculateFitZoom]);

  // Handle keyboard events for panning (space key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isSpacePressed) {
        e.preventDefault();
        setIsSpacePressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpacePressed(false);
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isSpacePressed]);

  // Handle wheel event for zooming to cursor position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const stage = stageRef.current;
      if (!stage) return;

      const scaleBy = 1.1;
      const oldScale = editorState.zoom;
      const pointer = stage.getPointerPosition();

      if (!pointer) return;

      // Calculate position relative to stage
      const mousePointTo = {
        x: (pointer.x - stagePosition.x) / oldScale,
        y: (pointer.y - stagePosition.y) / oldScale,
      };

      // Calculate new scale
      const direction = e.deltaY > 0 ? -1 : 1;
      const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      const clampedScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newScale));

      // Calculate new position to zoom to cursor
      const newPos = {
        x: pointer.x - mousePointTo.x * clampedScale,
        y: pointer.y - mousePointTo.y * clampedScale,
      };

      setStagePosition(newPos);
      setEditorState(prev => ({ ...prev, zoom: clampedScale }));
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [editorState.zoom, stagePosition]);

  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && stageRef.current) {
      const stage = stageRef.current;
      const selectedNodes = editorState.selectedElementIds
        .map(id => stage.findOne(`#${id}`))
        .filter(node => node);

      transformerRef.current.nodes(selectedNodes);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [editorState.selectedElementIds]);

  const saveToHistory = useCallback(() => {
    setHistory(prev => ({
      past: [...prev.past, elements],
      future: [],
    }));
  }, [elements]);

  const handleSave = useCallback(() => {
    if (onSave && stageRef.current) {
      try {
        // Clamp element coordinates to canvas bounds before saving
        const clampedElements = elements.map(el => ({
          ...el,
          x: Math.max(0, Math.min(CANVAS_WIDTH, el.x)),
          y: Math.max(0, Math.min(CANVAS_HEIGHT, el.y)),
        }));

        // Hide nodes and devices layers before exporting PNG (save only schema)
        const stage = stageRef.current;
        const layers = stage.getLayers();
        const hiddenLayers: Konva.Layer[] = [];

        // Hide layers 2+ (index 0 = background, index 1 = schema elements, index 2+ = nodes/devices)
        layers.forEach((layer, index) => {
          if (index >= 2 && layer.visible()) {
            layer.visible(false);
            hiddenLayers.push(layer);
          }
        });

        // Save current zoom and position
        const currentZoom = editorState.zoom;
        const currentPosition = { ...stagePosition };

        // Reset zoom and position for export
        stage.scaleX(1);
        stage.scaleY(1);
        stage.x(0);
        stage.y(0);

        // Export only the canvas area (CANVAS_WIDTH x CANVAS_HEIGHT)
        const dataUrl = stage.toDataURL({
          x: 0,
          y: 0,
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          pixelRatio: 2,
        });

        // Restore zoom and position
        stage.scaleX(currentZoom);
        stage.scaleY(currentZoom);
        stage.x(currentPosition.x);
        stage.y(currentPosition.y);

        // Restore visibility of hidden layers
        hiddenLayers.forEach(layer => layer.visible(true));

        onSave(clampedElements, canvasBgColor, dataUrl);
      } catch (err) {
        console.error('Failed to save:', err);
        // Try saving without image if toDataURL fails
        onSave(elements, canvasBgColor, '');
      }
    }
  }, [elements, canvasBgColor, onSave, editorState.zoom, stagePosition]);

  const handleUndo = useCallback(() => {
    if (history.past.length === 0) return;
    const previous = history.past[history.past.length - 1];
    const newPast = history.past.slice(0, -1);
    setHistory({
      past: newPast,
      future: [elements, ...history.future],
    });
    setElements(previous);
  }, [history, elements]);

  const handleRedo = useCallback(() => {
    if (history.future.length === 0) return;
    const next = history.future[0];
    const newFuture = history.future.slice(1);
    setHistory({
      past: [...history.past, elements],
      future: newFuture,
    });
    setElements(next);
  }, [history, elements]);

  const handleCopy = useCallback(() => {
    const selectedElements = elements.filter(el =>
      editorState.selectedElementIds.includes(el.id)
    );
    if (selectedElements.length > 0) {
      setEditorState(prev => ({ ...prev, clipboard: selectedElements }));
    }
  }, [elements, editorState.selectedElementIds]);

  const handlePaste = useCallback(() => {
    if (editorState.clipboard.length === 0) return;

    saveToHistory();
    const newElements = editorState.clipboard.map(el => ({
      ...el,
      id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x: el.x + 20,
      y: el.y + 20,
    }));

    setElements(prev => [...prev, ...newElements]);
    setEditorState(prev => ({
      ...prev,
      selectedElementIds: newElements.map(el => el.id),
    }));
  }, [editorState.clipboard, saveToHistory]);

  const handleDeleteSelected = useCallback(() => {
    saveToHistory();
    const newElements = elements.filter(el => !editorState.selectedElementIds.includes(el.id));
    setElements(newElements);
    setEditorState(prev => ({ ...prev, selectedElementIds: [] }));
  }, [elements, editorState.selectedElementIds, saveToHistory]);

  const handleToggleLock = useCallback(() => {
    saveToHistory();
    const newElements = elements.map(el => {
      if (editorState.selectedElementIds.includes(el.id)) {
        return { ...el, locked: !el.locked };
      }
      return el;
    });
    setElements(newElements);
  }, [elements, editorState.selectedElementIds, saveToHistory]);

  const handleToolChange = (tool: EditorTool) => {
    // Cancel polygon drawing if switching tools
    if (isDrawingPolygon && tool !== 'polygon') {
      setIsDrawingPolygon(false);
      setPolygonPoints([]);
    }
    setEditorState(prev => ({ ...prev, tool, selectedElementIds: [] }));
  };

  const handleZoomChange = (zoom: number) => {
    setEditorState(prev => ({ ...prev, zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)) }));
  };

  const handleExport = useCallback(() => {
    if (stageRef.current && onExportImage) {
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
      onExportImage(dataUrl);
    }
  }, [onExportImage]);

  // Get actual position on canvas (accounting for pan and zoom)
  const getCanvasPosition = (e: any) => {
    const stage = stageRef.current;
    if (!stage) return { x: 0, y: 0 };

    const pos = stage.getPointerPosition();
    if (!pos) return { x: 0, y: 0 };

    // Convert screen position to canvas position accounting for pan (stagePosition) and zoom
    const x = (pos.x - stagePosition.x) / editorState.zoom;
    const y = (pos.y - stagePosition.y) / editorState.zoom;

    return { x, y };
  };

  const finishPolygon = useCallback(() => {
    if (polygonPoints.length >= 6) {
      saveToHistory();
      const newElement: SchemaElement = {
        id: `element-${Date.now()}`,
        type: 'polygon',
        x: 0,
        y: 0,
        points: polygonPoints,
        fill: editorState.fillColor,
        stroke: editorState.strokeColor,
        strokeWidth: editorState.strokeWidth,
      };
      setElements(prev => [...prev, newElement]);
    }
    setIsDrawingPolygon(false);
    setPolygonPoints([]);
  }, [polygonPoints, editorState, saveToHistory]);

  // Keyboard shortcuts - must be after all handler definitions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isReadOnly || showTextInput) return;

      // Support both Ctrl (Windows/Linux) and Cmd (Mac)
      const isModifierKey = e.ctrlKey || e.metaKey;

      // Ctrl/Cmd+S - Save
      if (isModifierKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      // Ctrl/Cmd+Z - Undo
      if (isModifierKey && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl/Cmd+Shift+Z - Redo
      if (isModifierKey && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        handleRedo();
      }
      // Ctrl/Cmd+C - Copy
      if (isModifierKey && e.key === 'c') {
        e.preventDefault();
        handleCopy();
      }
      // Ctrl/Cmd+V - Paste
      if (isModifierKey && e.key === 'v') {
        e.preventDefault();
        handlePaste();
      }
      // Delete - Remove selected
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (editorState.selectedElementIds.length > 0 && !showTextInput) {
          e.preventDefault();
          handleDeleteSelected();
        }
      }
      // Escape - Deselect or cancel polygon/template/device
      if (e.key === 'Escape') {
        if (isDrawingPolygon) {
          setIsDrawingPolygon(false);
          setPolygonPoints([]);
        }
        // Cancel template selection
        if (selectedTemplate) {
          setSelectedTemplate(null);
          setSelectedTemplateId(null);
        }
        // Cancel device selection
        if (selectedDeviceForPlacement) {
          setSelectedDeviceForPlacement(null);
          setSelectedDeviceIndex(null);
        }
        setEditorState(prev => ({ ...prev, selectedElementIds: [], tool: 'select' }));
      }
      // L - Lock/Unlock
      if (e.key === 'l' && editorState.selectedElementIds.length > 0) {
        handleToggleLock();
      }
      // Enter - Finish polygon
      if (e.key === 'Enter' && isDrawingPolygon && polygonPoints.length >= 6) {
        finishPolygon();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReadOnly, showTextInput, isDrawingPolygon, polygonPoints, selectedTemplate, selectedDeviceForPlacement, editorState.selectedElementIds, handleSave, handleUndo, handleRedo, handleCopy, handlePaste, handleDeleteSelected, handleToggleLock, finishPolygon]);

  // Handle mouse down for drawing
  const handleMouseDown = (e: any) => {
    // Handle panning with space key or middle mouse button
    const nativeEvent = e.evt;
    if (isSpacePressed || nativeEvent?.button === 1) {
      nativeEvent?.preventDefault();
      setIsPanning(true);
      const stage = stageRef.current;
      if (stage) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          setLastPanPosition({ x: pointerPos.x, y: pointerPos.y });
        }
      }
      return;
    }

    if (isReadOnly) return;

    const pos = getCanvasPosition(e);
    // Consider click on stage OR background rect as "empty" click
    const targetName = e.target.name?.() || '';
    const clickedOnEmpty = e.target === e.target.getStage() || targetName === 'background';

    // Handle node placement in nodes mode
    if (mode === 'nodes' && nodeToPlace && clickedOnEmpty) {
      handleNodePlacement(pos.x, pos.y);
      return;
    }

    // Handle device placement in devices mode
    if (mode === 'devices' && selectedDeviceForPlacement && clickedOnEmpty) {
      handleDevicePlacementOnCanvas(pos.x, pos.y);
      return;
    }

    // Only handle schema drawing in schema mode
    if (mode !== 'schema') return;

    if (editorState.tool === 'select') {
      // If a template is selected, place it on canvas
      if (selectedTemplate) {
        handleTemplatePlacement(pos.x, pos.y);
        return;
      }
      if (clickedOnEmpty) {
        setEditorState(prev => ({ ...prev, selectedElementIds: [] }));
      }
      return;
    }

    if (editorState.tool === 'text') {
      setTextInputPosition(pos);
      setTextInputValue('');
      setShowTextInput(true);
      return;
    }

    if (editorState.tool === 'polygon') {
      if (!isDrawingPolygon) {
        setIsDrawingPolygon(true);
        setPolygonPoints([pos.x, pos.y]);
      } else {
        // Add point to polygon
        setPolygonPoints(prev => [...prev, pos.x, pos.y]);
      }
      return;
    }

    if (editorState.tool === 'eraser') {
      // Find and delete element at position
      const stage = stageRef.current;
      if (stage) {
        const shape = stage.getIntersection(stage.getPointerPosition());
        if (shape && shape.id()) {
          saveToHistory();
          setElements(prev => prev.filter(el => el.id !== shape.id()));
        }
      }
      return;
    }

    if (['rect', 'circle', 'line', 'freehand', 'border', 'road', 'trail'].includes(editorState.tool)) {
      setIsDrawing(true);
      const isBorder = editorState.tool === 'border';
      const isRoad = editorState.tool === 'road';
      const isTrail = editorState.tool === 'trail';
      const newElement: SchemaElement = {
        id: `element-${Date.now()}`,
        type: isBorder ? 'rect' : editorState.tool as SchemaElement['type'],
        x: pos.x,
        y: pos.y,
        fill: isBorder ? 'transparent' : (editorState.tool !== 'line' && !isRoad && !isTrail ? editorState.fillColor : undefined),
        stroke: isBorder ? '#ff6600' : (isRoad ? '#a0522d' : (isTrail ? '#8B7355' : editorState.strokeColor)),
        strokeWidth: isBorder ? 3 : (isRoad ? 8 : (isTrail ? 4 : editorState.strokeWidth)), // Thinner for trails
        width: 0,
        height: 0,
        points: editorState.tool === 'line' || editorState.tool === 'freehand' || isRoad || isTrail ? [0, 0] : undefined,
        label: isBorder ? 'border' : (isRoad ? 'road' : (isTrail ? 'trail' : undefined)),
      };
      setCurrentElement(newElement);
    }
  };

  // Handle mouse move for drawing or panning
  const handleMouseMove = (e: any) => {
    // Handle panning
    if (isPanning) {
      const stage = stageRef.current;
      if (stage) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          const dx = pointerPos.x - lastPanPosition.x;
          const dy = pointerPos.y - lastPanPosition.y;
          setStagePosition(prev => ({
            x: prev.x + dx,
            y: prev.y + dy,
          }));
          setLastPanPosition({ x: pointerPos.x, y: pointerPos.y });
        }
      }
      return;
    }

    if (!isDrawing || !currentElement) return;

    const pos = getCanvasPosition(e);

    if (currentElement.type === 'rect') {
      setCurrentElement({
        ...currentElement,
        width: pos.x - currentElement.x,
        height: pos.y - currentElement.y,
      });
    } else if (currentElement.type === 'circle') {
      const dx = pos.x - currentElement.x;
      const dy = pos.y - currentElement.y;
      setCurrentElement({
        ...currentElement,
        radius: Math.sqrt(dx * dx + dy * dy),
      });
    } else if (currentElement.type === 'line') {
      setCurrentElement({
        ...currentElement,
        points: [0, 0, pos.x - currentElement.x, pos.y - currentElement.y],
      });
    } else if (currentElement.type === 'freehand' || currentElement.type === 'road' || currentElement.type === 'trail') {
      const points = currentElement.points || [];
      setCurrentElement({
        ...currentElement,
        points: [...points, pos.x - currentElement.x, pos.y - currentElement.y],
      });
    }
  };

  // Handle mouse up for drawing or panning
  const handleMouseUp = () => {
    // Stop panning
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (!isDrawing || !currentElement) return;

    setIsDrawing(false);

    // Only add if element has size
    const hasSize =
      (currentElement.type === 'rect' && Math.abs(currentElement.width || 0) > 5 && Math.abs(currentElement.height || 0) > 5) ||
      (currentElement.type === 'circle' && (currentElement.radius || 0) > 5) ||
      (currentElement.type === 'line' && (currentElement.points?.length || 0) > 2) ||
      (currentElement.type === 'freehand' && (currentElement.points?.length || 0) > 4) ||
      (currentElement.type === 'road' && (currentElement.points?.length || 0) > 4) ||
      (currentElement.type === 'trail' && (currentElement.points?.length || 0) > 4);

    if (hasSize) {
      saveToHistory();
      setElements([...elements, currentElement]);
    }

    setCurrentElement(null);
  };

  // Handle double click to finish polygon or edit text
  const handleDoubleClick = (e: any) => {
    // Finish polygon if drawing
    if (isDrawingPolygon && polygonPoints.length >= 6) {
      finishPolygon();
      return;
    }

    // Check if clicked on a text element
    const clickedOn = e.target;
    const elementId = clickedOn.getAttr('id');

    if (elementId) {
      const element = elements.find(el => el.id === elementId);
      if (element && element.type === 'text') {
        // Open dialog with current text
        setEditingTextId(elementId);
        setTextInputValue(element.text || '');
        setTextInputPosition({ x: element.x || 0, y: element.y || 0 });
        setShowTextInput(true);
      }
    }
  };

  // Handle text input submit
  const handleTextSubmit = () => {
    if (textInputValue.trim()) {
      saveToHistory();

      if (editingTextId) {
        // Edit existing text element
        setElements(prev => prev.map(el =>
          el.id === editingTextId
            ? { ...el, text: textInputValue }
            : el
        ));
      } else {
        // Create new text element
        const newElement: SchemaElement = {
          id: `element-${Date.now()}`,
          type: 'text',
          x: textInputPosition.x,
          y: textInputPosition.y,
          text: textInputValue,
          width: 1,  // Scale factor for text size
          fill: editorState.strokeColor,
          stroke: editorState.strokeColor,
          strokeWidth: 0,
        };
        setElements(prev => [...prev, newElement]);
      }
    }

    // Reset
    setShowTextInput(false);
    setTextInputValue('');
    setEditingTextId(null);
  };

  // Handle template selection from library
  const handleTemplateSelect = (template: Partial<SchemaElement>) => {
    setSelectedTemplate(template);
    setSelectedTemplateId(template.id || null);
    // Switch to select tool to place template
    setEditorState(prev => ({ ...prev, tool: 'select' }));
  };

  // Handle template placement on canvas
  const handleTemplatePlacement = (x: number, y: number) => {
    if (!selectedTemplate) return;


    saveToHistory();
    const isIconElement = selectedTemplate.type === 'icon';
    const newElement: SchemaElement = {
      id: `element-${Date.now()}`,
      type: selectedTemplate.type || 'rect',
      x,
      y,
      width: isIconElement ? 1 : selectedTemplate.width,   // For icons, width = X scale (default 1)
      height: isIconElement ? 1 : selectedTemplate.height, // For icons, height = Y scale (default 1)
      radius: selectedTemplate.radius,
      points: selectedTemplate.points ? [...selectedTemplate.points] : undefined,
      fill: selectedTemplate.fill || editorState.fillColor,
      stroke: selectedTemplate.stroke || editorState.strokeColor,
      strokeWidth: selectedTemplate.strokeWidth || editorState.strokeWidth,
      label: selectedTemplate.label,
      iconType: selectedTemplate.iconType,  // Pass iconType for proper icon rendering
    };

    setElements(prev => [...prev, newElement]);

    // Clear template selection after placement
    setSelectedTemplate(null);
    setSelectedTemplateId(null);
  };

  // Handle node placement on canvas
  const handleNodePlacement = (x: number, y: number) => {
    if (!nodeToPlace || !onNodePositionUpdate) return;

    onNodePositionUpdate(nodeToPlace.id, x, y, nodeToPlace.scale, nodeToPlace.locked);
    setNodeToPlace(null);
    setSelectedNodeId(null);
  };

  // Handle node position update from manager
  const handleNodePositionUpdateFromManager = (
    nodeId: string,
    x: number,
    y: number,
    scale: number,
    locked: boolean
  ) => {
    if (onNodePositionUpdate) {
      onNodePositionUpdate(nodeId, x, y, scale, locked);
    }
  };

  // Handle placing node on canvas - place at center with default scale 1
  const handlePlaceNodeOnCanvas = (node: NodeWithPosition) => {
    if (onNodePositionUpdate) {
      // Place node at center of canvas with scale 1.0
      const centerX = CANVAS_WIDTH / 2;
      const centerY = CANVAS_HEIGHT / 2;
      const defaultScale = 1.0;
      onNodePositionUpdate(node.id, centerX, centerY, defaultScale, node.locked);
    }
    setSelectedNodeId(node.id);
  };

  // Handle saving operation template
  const handleSaveOperationTemplate = async (template: OperationTemplateRequest) => {
    try {
      // Check if template has an id - if yes, update; otherwise create new
      if (template.id) {
        const updated = await updateOperationTemplate(template.id, template);
        setOperationTemplates(prev => prev.map(t => t.id === template.id ? updated : t));
      } else {
        const created = await createOperationTemplate(mapId, template);
        setOperationTemplates(prev => [...prev, created]);
        setSelectedOperationTemplateId(created.id);
      }
    } catch (err) {
      console.error('Failed to save template:', err);
      throw err;
    }
  };

  // Handle deleting operation template
  const handleDeleteOperationTemplate = async (templateId: string) => {
    try {
      await deleteOperationTemplate(templateId);
      setOperationTemplates(prev => prev.filter(t => t.id !== templateId));
      if (selectedOperationTemplateId === templateId) {
        setSelectedOperationTemplateId(null);
      }
    } catch (err) {
      console.error('Failed to delete template:', err);
      throw err;
    }
  };

  // Handle device selection for placement
  const handleDeviceSelect = (device: TemplateDevicePosition | null, index: number | null) => {
    if (device && index !== null) {
      // Сразу размещаем устройство в центре холста
      const centerX = CANVAS_WIDTH / 2;
      const centerY = CANVAS_HEIGHT / 2;

      const updatedDevice: TemplateDevicePosition = {
        ...device,
        positionX: centerX,
        positionY: centerY,
      };

      // Добавляем или обновляем позицию устройства
      setDevicePositions(prev => {
        const existingIndex = prev.findIndex(d => d.fmdId === updatedDevice.fmdId);
        if (existingIndex >= 0) {
          const newPositions = [...prev];
          newPositions[existingIndex] = updatedDevice;
          return newPositions;
        }
        return [...prev, updatedDevice];
      });
    }

    setSelectedDeviceForPlacement(null);
    setSelectedDeviceIndex(null);
  };

  // Handle device placement on canvas
  const handleDevicePlacementOnCanvas = (x: number, y: number) => {
    if (!selectedDeviceForPlacement || selectedDeviceIndex === null) return;

    // Координаты уже в системе координат холста (getCanvasPosition учитывает zoom и pan)
    const updatedDevice: TemplateDevicePosition = {
      ...selectedDeviceForPlacement,
      positionX: x,
      positionY: y,
    };


    // Update or add device position (используем fmdId для уникальности)
    setDevicePositions(prev => {
      const existingIndex = prev.findIndex(
        d => d.fmdId === updatedDevice.fmdId
      );
      if (existingIndex >= 0) {
        const newPositions = [...prev];
        newPositions[existingIndex] = updatedDevice;
        return newPositions;
      }
      return [...prev, updatedDevice];
    });

    // Clear selection
    setSelectedDeviceForPlacement(null);
    setSelectedDeviceIndex(null);
  };

  // Handle element selection
  const handleElementClick = (elementId: string, e: any) => {
    if (mode !== 'schema' || editorState.tool !== 'select') return;

    e.cancelBubble = true;
    const isMultiSelect = e.evt?.shiftKey;

    if (isMultiSelect) {
      setEditorState(prev => ({
        ...prev,
        selectedElementIds: prev.selectedElementIds.includes(elementId)
          ? prev.selectedElementIds.filter(id => id !== elementId)
          : [...prev.selectedElementIds, elementId],
      }));
    } else {
      setEditorState(prev => ({
        ...prev,
        selectedElementIds: [elementId],
      }));
    }
  };

  // Handle element drag start - store initial positions of all selected elements
  const handleElementDragStart = (elementId: string, e: any) => {
    const selectedIds = editorState.selectedElementIds;

    // Only handle multi-drag if the dragged element is part of selection and there are multiple selected
    if (selectedIds.includes(elementId) && selectedIds.length > 1) {
      isDraggingMultipleRef.current = true;

      // Store initial positions of all selected elements
      const positions: Record<string, { x: number; y: number }> = {};
      elements.forEach(el => {
        if (selectedIds.includes(el.id)) {
          positions[el.id] = { x: el.x, y: el.y };
        }
      });
      dragStartPositionsRef.current = positions;
    }
  };

  // Handle element drag move - move all selected elements together
  const handleElementDragMove = (elementId: string, e: any) => {
    if (!isDraggingMultipleRef.current) return;

    const selectedIds = editorState.selectedElementIds;
    if (!selectedIds.includes(elementId) || selectedIds.length <= 1) return;

    const stage = stageRef.current;
    if (!stage) return;

    const dragStartPositions = dragStartPositionsRef.current;

    // Calculate delta from drag start position
    const startPos = dragStartPositions[elementId];
    if (!startPos) return;

    const currentX = e.target.x();
    const currentY = e.target.y();
    const deltaX = currentX - startPos.x;
    const deltaY = currentY - startPos.y;

    // Move all other selected elements
    selectedIds.forEach(id => {
      if (id !== elementId) {
        const node = stage.findOne(`#${id}`);
        if (node && dragStartPositions[id]) {
          const newX = Math.max(0, Math.min(CANVAS_WIDTH, dragStartPositions[id].x + deltaX));
          const newY = Math.max(0, Math.min(CANVAS_HEIGHT, dragStartPositions[id].y + deltaY));
          node.x(newX);
          node.y(newY);
        }
      }
    });
  };

  // Handle element drag end
  const handleElementDragEnd = (elementId: string, e: any) => {
    const selectedIds = editorState.selectedElementIds;
    const dragStartPositions = dragStartPositionsRef.current;

    // Clamp coordinates to canvas bounds
    const newX = Math.max(0, Math.min(CANVAS_WIDTH, e.target.x()));
    const newY = Math.max(0, Math.min(CANVAS_HEIGHT, e.target.y()));

    saveToHistory();

    if (isDraggingMultipleRef.current && selectedIds.includes(elementId) && selectedIds.length > 1) {
      // Calculate delta from original position
      const startPos = dragStartPositions[elementId];
      const deltaX = startPos ? newX - startPos.x : 0;
      const deltaY = startPos ? newY - startPos.y : 0;

      // Update all selected elements
      setElements(elements.map(el => {
        if (selectedIds.includes(el.id)) {
          if (el.id === elementId) {
            return { ...el, x: newX, y: newY };
          } else {
            const elStartPos = dragStartPositions[el.id];
            if (elStartPos) {
              return {
                ...el,
                x: Math.max(0, Math.min(CANVAS_WIDTH, elStartPos.x + deltaX)),
                y: Math.max(0, Math.min(CANVAS_HEIGHT, elStartPos.y + deltaY)),
              };
            }
          }
        }
        return el;
      }));

      // Reset multi-drag state
      isDraggingMultipleRef.current = false;
      dragStartPositionsRef.current = {};
    } else {
      // Single element drag
      setElements(elements.map(el => {
        if (el.id === elementId) {
          return { ...el, x: newX, y: newY };
        }
        return el;
      }));
    }
  };

  // Handle element transform
  const handleTransformEnd = (elementId: string, e: any) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Reset scale and apply to dimensions
    node.scaleX(1);
    node.scaleY(1);

    // Clamp coordinates to canvas bounds
    const clampX = (x: number) => Math.max(0, Math.min(CANVAS_WIDTH, x));
    const clampY = (y: number) => Math.max(0, Math.min(CANVAS_HEIGHT, y));

    saveToHistory();
    setElements(elements.map(el => {
      if (el.id === elementId) {
        if (el.type === 'rect') {
          return {
            ...el,
            x: clampX(node.x()),
            y: clampY(node.y()),
            width: (el.width || 0) * scaleX,
            height: (el.height || 0) * scaleY,
            rotation: node.rotation(),
          };
        } else if (el.type === 'circle') {
          return {
            ...el,
            x: clampX(node.x()),
            y: clampY(node.y()),
            radius: (el.radius || 0) * Math.max(scaleX, scaleY),
          };
        } else if (el.type === 'icon') {
          // For icons, scaleX/scaleY from transformer is the NEW total scale
          // (Konva updates scaleX/scaleY props when transforming)
          return {
            ...el,
            x: clampX(node.x()),
            y: clampY(node.y()),
            width: Math.max(0.3, Math.min(5, scaleX)),   // X scale (0.3x - 5x)
            height: Math.max(0.3, Math.min(5, scaleY)),  // Y scale (0.3x - 5x)
            rotation: node.rotation(),
          };
        } else if (el.type === 'text') {
          // For text, store scale in width (like icons)
          const currentScale = el.width || 1;
          const avgScale = (scaleX + scaleY) / 2;
          return {
            ...el,
            x: clampX(node.x()),
            y: clampY(node.y()),
            width: Math.max(0.5, Math.min(5, currentScale * avgScale)),
            rotation: node.rotation(),
          };
        }
        return { ...el, x: clampX(node.x()), y: clampY(node.y()) };
      }
      return el;
    }));
  };

  // Handle node drag
  const handleNodeDragEnd = (node: NodeWithPosition, e: any) => {
    if (onNodePositionUpdate && !node.locked) {
      const newX = e.target.x();
      const newY = e.target.y();
      onNodePositionUpdate(node.id, newX, newY, node.scale, node.locked);
    }
  };

  // Calculate background image position to preserve aspect ratio
  const getBackgroundImageProps = () => {
    if (!backgroundImage) return null;

    const imgRatio = backgroundImage.width / backgroundImage.height;
    const canvasRatio = CANVAS_WIDTH / CANVAS_HEIGHT;

    let imgWidth: number;
    let imgHeight: number;

    if (imgRatio > canvasRatio) {
      imgWidth = CANVAS_WIDTH;
      imgHeight = CANVAS_WIDTH / imgRatio;
    } else {
      imgHeight = CANVAS_HEIGHT;
      imgWidth = CANVAS_HEIGHT * imgRatio;
    }

    return {
      image: backgroundImage,
      x: (CANVAS_WIDTH - imgWidth) / 2,
      y: (CANVAS_HEIGHT - imgHeight) / 2,
      width: imgWidth,
      height: imgHeight,
    };
  };


  // Render icon element on canvas (matching TemplateLibrary SVGs)
  const renderIcon = (element: SchemaElement, scale: number, isSelected: boolean) => {
    const iconType = element.iconType;
    if (!iconType) return null;

    const baseSize = 48; // Base icon size
    const iconScaleX = element.width || 1;   // X scale multiplier
    const iconScaleY = element.height || 1;  // Y scale multiplier
    const scaledSize = baseSize * scale;     // Base scaled size for drawing

    const commonGroupProps = {
      id: element.id,
      x: element.x * scale,
      y: element.y * scale,
      scaleX: iconScaleX,  // Apply X stretch
      scaleY: iconScaleY,  // Apply Y stretch
      draggable: !isReadOnly && !element.locked && mode === 'schema' && editorState.tool === 'select',
      onClick: (e: any) => handleElementClick(element.id, e),
      onTap: (e: any) => handleElementClick(element.id, e),
      onDragStart: (e: any) => handleElementDragStart(element.id, e),
      onDragMove: (e: any) => handleElementDragMove(element.id, e),
      onDragEnd: (e: any) => handleElementDragEnd(element.id, e),
      onTransformEnd: (e: any) => handleTransformEnd(element.id, e),
      rotation: element.rotation || 0,
    };

    const selectionStroke = isSelected ? '#00ff00' : undefined;
    const sw = 2 * scale; // strokeWidth

    switch (iconType) {
      case 'tree':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Tree crown */}
            <Circle
              x={scaledSize * 0.5}
              y={scaledSize * 0.42}
              radius={scaledSize * 0.29}
              fill="rgba(34, 139, 34, 0.6)"
              stroke={selectionStroke || "#228B22"}
              strokeWidth={sw}
            />
            {/* Tree trunk */}
            <Rect
              x={scaledSize * 0.44}
              y={scaledSize * 0.67}
              width={scaledSize * 0.125}
              height={scaledSize * 0.21}
              fill="#8B4513"
              stroke="#8B4513"
              strokeWidth={sw * 0.5}
            />
          </Group>
        );

      case 'bush':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Circle x={scaledSize * 0.375} y={scaledSize * 0.58} radius={scaledSize * 0.21} fill="rgba(46, 139, 87, 0.5)" stroke={selectionStroke || "#2E8B57"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.625} y={scaledSize * 0.58} radius={scaledSize * 0.21} fill="rgba(46, 139, 87, 0.5)" stroke={selectionStroke || "#2E8B57"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.42} radius={scaledSize * 0.21} fill="rgba(46, 139, 87, 0.5)" stroke={selectionStroke || "#2E8B57"} strokeWidth={sw} />
          </Group>
        );

      case 'building':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Roof /\ */}
            <Line
              points={[
                scaledSize * 0.17, scaledSize * 0.33,  // Left corner
                scaledSize * 0.5, scaledSize * 0.083,  // Top peak
                scaledSize * 0.83, scaledSize * 0.33,  // Right corner
              ]}
              fill="rgba(139, 69, 19, 0.5)"
              stroke={selectionStroke || "#8B4513"}
              strokeWidth={sw}
              closed={true}
            />
            {/* Building body */}
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.33} width={scaledSize * 0.67} height={scaledSize * 0.58} fill="rgba(100, 80, 60, 0.35)" stroke={selectionStroke || "#8B7355"} strokeWidth={sw} />
            {/* Windows */}
            <Rect x={scaledSize * 0.29} y={scaledSize * 0.46} width={scaledSize * 0.17} height={scaledSize * 0.17} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.54} y={scaledSize * 0.46} width={scaledSize * 0.17} height={scaledSize * 0.17} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.29} y={scaledSize * 0.71} width={scaledSize * 0.17} height={scaledSize * 0.17} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.54} y={scaledSize * 0.71} width={scaledSize * 0.17} height={scaledSize * 0.17} fill="rgba(51, 51, 51, 0.5)" />
          </Group>
        );

      case 'building-large':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Roof /\ */}
            <Line
              points={[
                scaledSize * 0.083, scaledSize * 0.29,  // Left corner
                scaledSize * 0.5, scaledSize * 0.042,   // Top peak
                scaledSize * 0.917, scaledSize * 0.29,  // Right corner
              ]}
              fill="rgba(139, 69, 19, 0.5)"
              stroke={selectionStroke || "#8B4513"}
              strokeWidth={sw}
              closed={true}
            />
            {/* Building body */}
            <Rect x={scaledSize * 0.083} y={scaledSize * 0.29} width={scaledSize * 0.833} height={scaledSize * 0.625} fill="rgba(100, 80, 60, 0.35)" stroke={selectionStroke || "#8B7355"} strokeWidth={sw} />
            {/* Windows row 1 */}
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.375} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.354} y={scaledSize * 0.375} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.542} y={scaledSize * 0.375} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.729} y={scaledSize * 0.375} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            {/* Windows row 2 */}
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.583} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.354} y={scaledSize * 0.583} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.542} y={scaledSize * 0.583} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            <Rect x={scaledSize * 0.729} y={scaledSize * 0.583} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(51, 51, 51, 0.5)" />
            {/* Door */}
            <Rect x={scaledSize * 0.396} y={scaledSize * 0.75} width={scaledSize * 0.208} height={scaledSize * 0.167} fill="#A0522D" />
          </Group>
        );

      case 'room':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.25} width={scaledSize * 0.67} height={scaledSize * 0.58} fill="rgba(80, 80, 90, 0.5)" stroke={selectionStroke || "#666"} strokeWidth={sw} />
          </Group>
        );

      case 'wall':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Прозрачный rect для захвата и поворота */}
            <Rect x={0} y={0} width={scaledSize} height={scaledSize} fill="transparent" />
            <Rect x={scaledSize * 0.083} y={scaledSize * 0.42} width={scaledSize * 0.833} height={scaledSize * 0.17} fill="#555" stroke={selectionStroke || "#777"} strokeWidth={sw} />
          </Group>
        );

      case 'door':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect x={scaledSize * 0.33} y={scaledSize * 0.25} width={scaledSize * 0.33} height={scaledSize * 0.58} fill="rgba(139, 90, 43, 0.8)" stroke={selectionStroke || "#A0522D"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.58} y={scaledSize * 0.54} radius={scaledSize * 0.042} fill="#FFD700" />
          </Group>
        );

      case 'fence':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Прозрачный rect для захвата и поворота */}
            <Rect x={0} y={0} width={scaledSize} height={scaledSize} fill="transparent" />
            <Line points={[scaledSize * 0.083, scaledSize * 0.5, scaledSize * 0.917, scaledSize * 0.5]} stroke={selectionStroke || "#8B4513"} strokeWidth={sw * 2} />
            <Line points={[scaledSize * 0.21, scaledSize * 0.33, scaledSize * 0.21, scaledSize * 0.67]} stroke={selectionStroke || "#8B4513"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.5, scaledSize * 0.33, scaledSize * 0.5, scaledSize * 0.67]} stroke={selectionStroke || "#8B4513"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.79, scaledSize * 0.33, scaledSize * 0.79, scaledSize * 0.67]} stroke={selectionStroke || "#8B4513"} strokeWidth={sw} />
          </Group>
        );

      case 'barricade':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Прозрачный rect для захвата и поворота */}
            <Rect x={0} y={0} width={scaledSize} height={scaledSize} fill="transparent" />
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.375} width={scaledSize * 0.67} height={scaledSize * 0.25} fill="rgba(139, 69, 19, 0.7)" stroke={selectionStroke || "#A0522D"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.17, scaledSize * 0.375, scaledSize * 0.833, scaledSize * 0.625]} stroke={selectionStroke || "#A0522D"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.17, scaledSize * 0.625, scaledSize * 0.833, scaledSize * 0.375]} stroke={selectionStroke || "#A0522D"} strokeWidth={sw} />
          </Group>
        );

      case 'sandbags':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Прозрачный rect для захвата и поворота */}
            <Rect x={0} y={0} width={scaledSize} height={scaledSize} fill="transparent" />
            <Ellipse x={scaledSize * 0.33} y={scaledSize * 0.58} radiusX={scaledSize * 0.21} radiusY={scaledSize * 0.125} fill="rgba(194, 178, 128, 0.8)" stroke={selectionStroke || "#C2B280"} strokeWidth={sw} />
            <Ellipse x={scaledSize * 0.67} y={scaledSize * 0.58} radiusX={scaledSize * 0.21} radiusY={scaledSize * 0.125} fill="rgba(194, 178, 128, 0.8)" stroke={selectionStroke || "#C2B280"} strokeWidth={sw} />
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.42} radiusX={scaledSize * 0.21} radiusY={scaledSize * 0.125} fill="rgba(194, 178, 128, 0.8)" stroke={selectionStroke || "#C2B280"} strokeWidth={sw} />
          </Group>
        );

      case 'wire':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Прозрачный rect для захвата и поворота */}
            <Rect x={0} y={0} width={scaledSize} height={scaledSize} fill="transparent" />
            {/* Wavy wire - approximated with lines */}
            <Line
              points={[
                scaledSize * 0.083, scaledSize * 0.5,
                scaledSize * 0.25, scaledSize * 0.375,
                scaledSize * 0.42, scaledSize * 0.5,
                scaledSize * 0.58, scaledSize * 0.625,
                scaledSize * 0.75, scaledSize * 0.5,
                scaledSize * 0.917, scaledSize * 0.5,
              ]}
              stroke={selectionStroke || "#888"}
              strokeWidth={sw}
              tension={0.5}
            />
          </Group>
        );

      case 'crate':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect x={scaledSize * 0.21} y={scaledSize * 0.29} width={scaledSize * 0.58} height={scaledSize * 0.5} fill="rgba(139, 90, 43, 0.7)" stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.21, scaledSize * 0.54, scaledSize * 0.79, scaledSize * 0.54]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.5, scaledSize * 0.29, scaledSize * 0.5, scaledSize * 0.79]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
          </Group>
        );

      case 'vehicle':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Cab */}
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.25} width={scaledSize * 0.42} height={scaledSize * 0.17} fill="rgba(70, 70, 70, 0.7)" stroke={selectionStroke || "#555"} strokeWidth={sw} />
            {/* Body */}
            <Rect x={scaledSize * 0.125} y={scaledSize * 0.375} width={scaledSize * 0.75} height={scaledSize * 0.33} fill="rgba(70, 70, 70, 0.7)" stroke={selectionStroke || "#555"} strokeWidth={sw} />
            {/* Wheels */}
            <Circle x={scaledSize * 0.29} y={scaledSize * 0.71} radius={scaledSize * 0.083} fill="#333" stroke={selectionStroke || "#222"} strokeWidth={sw * 0.5} />
            <Circle x={scaledSize * 0.71} y={scaledSize * 0.71} radius={scaledSize * 0.083} fill="#333" stroke={selectionStroke || "#222"} strokeWidth={sw * 0.5} />
          </Group>
        );

      case 'rock':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.58} radiusX={scaledSize * 0.33} radiusY={scaledSize * 0.25} fill="rgba(105, 105, 105, 0.7)" stroke={selectionStroke || "#696969"} strokeWidth={sw} />
          </Group>
        );

      case 'tires':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Circle x={scaledSize * 0.33} y={scaledSize * 0.58} radius={scaledSize * 0.21} fill="rgba(40, 40, 40, 0.8)" stroke={selectionStroke || "#333"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.33} y={scaledSize * 0.58} radius={scaledSize * 0.08} fill="#1a1a1a" />
            <Circle x={scaledSize * 0.67} y={scaledSize * 0.58} radius={scaledSize * 0.21} fill="rgba(40, 40, 40, 0.8)" stroke={selectionStroke || "#333"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.67} y={scaledSize * 0.58} radius={scaledSize * 0.08} fill="#1a1a1a" />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.38} radius={scaledSize * 0.17} fill="rgba(40, 40, 40, 0.8)" stroke={selectionStroke || "#333"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.38} radius={scaledSize * 0.06} fill="#1a1a1a" />
          </Group>
        );

      case 'barrel':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.25} radiusX={scaledSize * 0.25} radiusY={scaledSize * 0.08} fill="rgba(100, 149, 237, 0.7)" stroke={selectionStroke || "#4169E1"} strokeWidth={sw} />
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.75} radiusX={scaledSize * 0.25} radiusY={scaledSize * 0.08} fill="rgba(100, 149, 237, 0.7)" stroke={selectionStroke || "#4169E1"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.25, scaledSize * 0.25, scaledSize * 0.25, scaledSize * 0.75]} stroke={selectionStroke || "#4169E1"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.75, scaledSize * 0.25, scaledSize * 0.75, scaledSize * 0.75]} stroke={selectionStroke || "#4169E1"} strokeWidth={sw} />
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.5} radiusX={scaledSize * 0.25} radiusY={scaledSize * 0.06} stroke={selectionStroke || "#4169E1"} strokeWidth={sw * 0.5} />
          </Group>
        );

      case 'ship':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Hull - корпус лодки \__/ */}
            <Line
              points={[
                scaledSize * 0.08, scaledSize * 0.5,
                scaledSize * 0.17, scaledSize * 0.75,
                scaledSize * 0.83, scaledSize * 0.75,
                scaledSize * 0.92, scaledSize * 0.5,
              ]}
              fill="rgba(139, 90, 43, 0.8)"
              stroke={selectionStroke || "#8B4513"}
              strokeWidth={sw * 1.5}
              closed
            />
            {/* Deck line - палуба */}
            <Line points={[scaledSize * 0.12, scaledSize * 0.5, scaledSize * 0.88, scaledSize * 0.5]} stroke={selectionStroke || "#A0522D"} strokeWidth={sw} />
            {/* Mast - мачта */}
            <Line points={[scaledSize * 0.5, scaledSize * 0.08, scaledSize * 0.5, scaledSize * 0.5]} stroke={selectionStroke || "#8B4513"} strokeWidth={sw * 1.5} />
            {/* Sail - парус */}
            <Line
              points={[
                scaledSize * 0.5, scaledSize * 0.1,
                scaledSize * 0.8, scaledSize * 0.3,
                scaledSize * 0.5, scaledSize * 0.45,
              ]}
              fill="rgba(255, 255, 255, 0.9)"
              stroke={selectionStroke || "#ddd"}
              strokeWidth={sw}
              closed
            />
            {/* Flag - флажок */}
            <Line
              points={[
                scaledSize * 0.5, scaledSize * 0.08,
                scaledSize * 0.6, scaledSize * 0.12,
                scaledSize * 0.5, scaledSize * 0.16,
              ]}
              fill="rgba(220, 20, 60, 0.9)"
              stroke={selectionStroke || "#DC143C"}
              strokeWidth={sw * 0.5}
              closed
            />
          </Group>
        );

      case 'airplane':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Fuselage */}
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.5} radiusX={scaledSize * 0.42} radiusY={scaledSize * 0.125} fill="rgba(192, 192, 192, 0.5)" stroke={selectionStroke || "#A9A9A9"} strokeWidth={sw} />
            {/* Tail */}
            <Line points={[scaledSize * 0.5, scaledSize * 0.08, scaledSize * 0.42, scaledSize * 0.42, scaledSize * 0.58, scaledSize * 0.42]} fill="rgba(192, 192, 192, 0.5)" stroke={selectionStroke || "#A9A9A9"} strokeWidth={sw} closed />
            {/* Nose */}
            <Line points={[scaledSize * 0.5, scaledSize * 0.92, scaledSize * 0.42, scaledSize * 0.58, scaledSize * 0.58, scaledSize * 0.58]} fill="rgba(192, 192, 192, 0.5)" stroke={selectionStroke || "#A9A9A9"} strokeWidth={sw} closed />
            {/* Left wing */}
            <Line points={[scaledSize * 0.08, scaledSize * 0.5, scaledSize * 0.33, scaledSize * 0.42, scaledSize * 0.33, scaledSize * 0.58]} fill="rgba(192, 192, 192, 0.5)" stroke={selectionStroke || "#A9A9A9"} strokeWidth={sw} closed />
            {/* Right wing */}
            <Line points={[scaledSize * 0.92, scaledSize * 0.5, scaledSize * 0.67, scaledSize * 0.42, scaledSize * 0.67, scaledSize * 0.58]} fill="rgba(192, 192, 192, 0.5)" stroke={selectionStroke || "#A9A9A9"} strokeWidth={sw} closed />
            {/* Cockpit */}
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.06} fill="#333" />
          </Group>
        );

      case 'spawn':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.25} width={scaledSize * 0.67} height={scaledSize * 0.54} fill="transparent" stroke={selectionStroke || element.stroke || "#1E90FF"} strokeWidth={sw} dash={[4, 2]} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.52} radius={scaledSize * 0.125} stroke={selectionStroke || element.stroke || "#1E90FF"} strokeWidth={sw} />
            {/* Cross lines */}
            <Line points={[scaledSize * 0.5, scaledSize * 0.31, scaledSize * 0.5, scaledSize * 0.4]} stroke={selectionStroke || element.stroke || "#1E90FF"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.5, scaledSize * 0.65, scaledSize * 0.5, scaledSize * 0.73]} stroke={selectionStroke || element.stroke || "#1E90FF"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.31, scaledSize * 0.52, scaledSize * 0.4, scaledSize * 0.52]} stroke={selectionStroke || element.stroke || "#1E90FF"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.6, scaledSize * 0.52, scaledSize * 0.69, scaledSize * 0.52]} stroke={selectionStroke || element.stroke || "#1E90FF"} strokeWidth={sw} />
          </Group>
        );

      case 'objective':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.33} fill="rgba(255, 215, 0, 0.3)" stroke={selectionStroke || "#FFD700"} strokeWidth={sw} dash={[4, 2]} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.17} stroke={selectionStroke || "#FFD700"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.063} fill="#FFD700" />
          </Group>
        );

      case 'safe':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.25} width={scaledSize * 0.67} height={scaledSize * 0.54} fill="rgba(50, 205, 50, 0.2)" stroke={selectionStroke || "#32CD32"} strokeWidth={sw} />
            {/* Checkmark - moved up */}
            <Line points={[scaledSize * 0.33, scaledSize * 0.42, scaledSize * 0.46, scaledSize * 0.52, scaledSize * 0.67, scaledSize * 0.30]} stroke={selectionStroke || "#32CD32"} strokeWidth={sw * 1.5} />
            {/* Label - moved up */}
            {element.label && (
              <Text
                x={scaledSize * 0.17}
                y={scaledSize * 0.58}
                width={scaledSize * 0.67}
                text={element.label}
                fontSize={scaledSize * 0.14}
                fontFamily="Arial"
                fontStyle="bold"
                fill="#32CD32"
                align="center"
              />
            )}
          </Group>
        );

      case 'danger':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.25} width={scaledSize * 0.67} height={scaledSize * 0.54} fill="rgba(255, 0, 0, 0.2)" stroke={selectionStroke || "#FF0000"} strokeWidth={sw} />
            {/* Exclamation mark - moved up */}
            <Line points={[scaledSize * 0.5, scaledSize * 0.32, scaledSize * 0.5, scaledSize * 0.48]} stroke={selectionStroke || "#FF0000"} strokeWidth={sw * 1.5} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.54} radius={scaledSize * 0.03} fill="#FF0000" />
            {/* Label - moved up */}
            {element.label && (
              <Text
                x={scaledSize * 0.17}
                y={scaledSize * 0.58}
                width={scaledSize * 0.67}
                text={element.label}
                fontSize={scaledSize * 0.12}
                fontFamily="Arial"
                fontStyle="bold"
                fill="#FF0000"
                align="center"
              />
            )}
          </Group>
        );

      case 'waypoint':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.17} fill="rgba(255, 255, 255, 0.8)" stroke={selectionStroke || "#fff"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.063} fill="#333" />
          </Group>
        );

      case 'target':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.29} stroke={selectionStroke || "#FFD700"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.17} stroke={selectionStroke || "#FFD700"} strokeWidth={sw} />
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.5} radius={scaledSize * 0.063} fill="#FFD700" />
          </Group>
        );

      case 'flag':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Pole */}
            <Line points={[scaledSize * 0.33, scaledSize * 0.83, scaledSize * 0.33, scaledSize * 0.21]} stroke={selectionStroke || element.stroke || "#1E90FF"} strokeWidth={sw * 1.5} />
            {/* Flag */}
            <Line
              points={[
                scaledSize * 0.375, scaledSize * 0.21,
                scaledSize * 0.75, scaledSize * 0.375,
                scaledSize * 0.375, scaledSize * 0.54,
              ]}
              fill={element.fill || "rgba(30, 144, 255, 0.9)"}
              stroke={selectionStroke || element.stroke || "#1E90FF"}
              strokeWidth={sw}
              closed
            />
          </Group>
        );

      case 'bomb':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Circle x={scaledSize * 0.5} y={scaledSize * 0.58} radius={scaledSize * 0.25} fill="rgba(255, 69, 0, 0.9)" stroke={selectionStroke || "#FF4500"} strokeWidth={sw} />
            {/* Fuse */}
            <Line points={[scaledSize * 0.5, scaledSize * 0.33, scaledSize * 0.5, scaledSize * 0.17]} stroke={selectionStroke || "#FF4500"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.42, scaledSize * 0.21, scaledSize * 0.5, scaledSize * 0.17, scaledSize * 0.58, scaledSize * 0.21]} stroke={selectionStroke || "#FF4500"} strokeWidth={sw} />
          </Group>
        );

      case 'road':
        // Точно как в шаблоне: viewBox 48x48, линии x=4 и x=44, y от 8 до 40
        const roadX = scaledSize * 0.083;       // 4/48
        const roadY = scaledSize * 0.167;       // 8/48
        const roadWidth = scaledSize * 0.833;   // 40/48 (от 4 до 44)
        const roadHeight = scaledSize * 0.667;  // 32/48 (от 8 до 40)
        const roadCenterX = scaledSize * 0.5;   // 24/48
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Прозрачный rect для возможности клика и перетаскивания */}
            <Rect
              x={roadX}
              y={roadY}
              width={roadWidth}
              height={roadHeight}
              fill="transparent"
            />
            {/* Левая граница */}
            <Line
              points={[roadX, roadY, roadX, roadY + roadHeight]}
              stroke={selectionStroke || "#8B4513"}
              strokeWidth={sw * 1.5}
            />
            {/* Правая граница */}
            <Line
              points={[roadX + roadWidth, roadY, roadX + roadWidth, roadY + roadHeight]}
              stroke={selectionStroke || "#8B4513"}
              strokeWidth={sw * 1.5}
            />
            {/* Пунктир посередине */}
            <Line
              points={[roadCenterX, roadY, roadCenterX, roadY + roadHeight]}
              stroke={selectionStroke || "#8B4513"}
              strokeWidth={sw}
              dash={[scaledSize * 0.1, scaledSize * 0.08]}
            />
          </Group>
        );

      case 'trail':
        // Trail - winding path with dashed line
        const trailStroke = selectionStroke || "#8B7355";
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Transparent hit area */}
            <Rect
              x={0}
              y={scaledSize * 0.3}
              width={scaledSize}
              height={scaledSize * 0.5}
              fill="transparent"
            />
            {/* Winding trail path */}
            <Line
              points={[
                scaledSize * 0.083, scaledSize * 0.833,  // Start
                scaledSize * 0.25, scaledSize * 0.667,
                scaledSize * 0.333, scaledSize * 0.75,
                scaledSize * 0.5, scaledSize * 0.583,
                scaledSize * 0.667, scaledSize * 0.667,
                scaledSize * 0.917, scaledSize * 0.417,  // End
              ]}
              stroke={trailStroke}
              strokeWidth={sw}
              dash={[scaledSize * 0.125, scaledSize * 0.083]}
              lineCap="round"
              lineJoin="round"
              tension={0.3}
            />
            {/* Small stones/markers along the trail */}
            <Circle x={scaledSize * 0.167} y={scaledSize * 0.792} radius={scaledSize * 0.042} fill={trailStroke} />
            <Circle x={scaledSize * 0.417} y={scaledSize * 0.667} radius={scaledSize * 0.031} fill={trailStroke} />
            <Circle x={scaledSize * 0.583} y={scaledSize * 0.625} radius={scaledSize * 0.042} fill={trailStroke} />
            <Circle x={scaledSize * 0.792} y={scaledSize * 0.5} radius={scaledSize * 0.031} fill={trailStroke} />
          </Group>
        );

      case 'exit':
        // Точно как в шаблоне: viewBox 48x48, rect 40x28 начиная с x=4, y=10
        const exitWidth = scaledSize * 0.833;  // 40/48
        const exitHeight = scaledSize * 0.583; // 28/48
        const exitX = scaledSize * 0.083;      // 4/48
        const exitY = scaledSize * 0.208;      // 10/48
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect
              x={exitX}
              y={exitY}
              width={exitWidth}
              height={exitHeight}
              fill="rgba(76, 175, 80, 0.3)"
              stroke={selectionStroke || "#4CAF50"}
              strokeWidth={sw}
              cornerRadius={scaledSize * 0.083}
            />
            <Text
              x={exitX}
              y={exitY + exitHeight * 0.25}
              width={exitWidth}
              height={exitHeight}
              text="EXIT"
              fontSize={scaledSize * 0.25}
              fontStyle="bold"
              fill={selectionStroke || "#4CAF50"}
              align="center"
              verticalAlign="middle"
            />
          </Group>
        );

      case 'helicopter':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Main body */}
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.58} radiusX={scaledSize * 0.25} radiusY={scaledSize * 0.17} fill="rgba(60, 60, 60, 0.6)" stroke={selectionStroke || "#444"} strokeWidth={sw} />
            {/* Cockpit */}
            <Ellipse x={scaledSize * 0.67} y={scaledSize * 0.58} radiusX={scaledSize * 0.125} radiusY={scaledSize * 0.125} fill="rgba(135, 206, 250, 0.5)" stroke={selectionStroke || "#87CEEB"} strokeWidth={sw} />
            {/* Tail */}
            <Rect x={scaledSize * 0.083} y={scaledSize * 0.54} width={scaledSize * 0.21} height={scaledSize * 0.083} fill="rgba(60, 60, 60, 0.6)" stroke={selectionStroke || "#444"} strokeWidth={sw * 0.5} />
            <Rect x={scaledSize * 0.042} y={scaledSize * 0.46} width={scaledSize * 0.083} height={scaledSize * 0.17} fill="rgba(60, 60, 60, 0.6)" stroke={selectionStroke || "#444"} strokeWidth={sw * 0.5} />
            {/* Main rotor */}
            <Line points={[scaledSize * 0.17, scaledSize * 0.375, scaledSize * 0.83, scaledSize * 0.375]} stroke={selectionStroke || "#444"} strokeWidth={sw} />
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.375} radiusX={scaledSize * 0.063} radiusY={scaledSize * 0.042} fill="#333" />
            {/* Skids */}
            <Line points={[scaledSize * 0.33, scaledSize * 0.75, scaledSize * 0.67, scaledSize * 0.75]} stroke={selectionStroke || "#444"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.375, scaledSize * 0.71, scaledSize * 0.375, scaledSize * 0.75]} stroke={selectionStroke || "#444"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.625, scaledSize * 0.71, scaledSize * 0.625, scaledSize * 0.75]} stroke={selectionStroke || "#444"} strokeWidth={sw} />
          </Group>
        );

      case 'river':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Wavy river shape */}
            <Line
              points={[
                scaledSize * 0.083, scaledSize * 0.5,
                scaledSize * 0.25, scaledSize * 0.375,
                scaledSize * 0.42, scaledSize * 0.5,
                scaledSize * 0.58, scaledSize * 0.625,
                scaledSize * 0.75, scaledSize * 0.5,
                scaledSize * 0.917, scaledSize * 0.5,
              ]}
              stroke={selectionStroke || "#1E90FF"}
              strokeWidth={sw}
              tension={0.5}
            />
            <Line
              points={[
                scaledSize * 0.083, scaledSize * 0.625,
                scaledSize * 0.25, scaledSize * 0.5,
                scaledSize * 0.42, scaledSize * 0.625,
                scaledSize * 0.58, scaledSize * 0.75,
                scaledSize * 0.75, scaledSize * 0.625,
                scaledSize * 0.917, scaledSize * 0.625,
              ]}
              stroke={selectionStroke || "#1E90FF"}
              strokeWidth={sw}
              tension={0.5}
            />
            {/* Fill between waves */}
            <Rect x={scaledSize * 0.083} y={scaledSize * 0.42} width={scaledSize * 0.833} height={scaledSize * 0.25} fill="rgba(30, 144, 255, 0.4)" stroke="transparent" />
          </Group>
        );

      case 'lake':
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Ellipse x={scaledSize * 0.5} y={scaledSize * 0.5} radiusX={scaledSize * 0.375} radiusY={scaledSize * 0.29} fill="rgba(30, 144, 255, 0.4)" stroke={selectionStroke || "#1E90FF"} strokeWidth={sw} />
            {/* Highlight */}
            <Ellipse x={scaledSize * 0.42} y={scaledSize * 0.46} radiusX={scaledSize * 0.083} radiusY={scaledSize * 0.042} fill="rgba(255, 255, 255, 0.3)" stroke="transparent" />
          </Group>
        );

      case 'bridge':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Bridge deck */}
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.375} width={scaledSize * 0.67} height={scaledSize * 0.25} fill="rgba(139, 90, 43, 0.8)" stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            {/* Support pillars */}
            <Line points={[scaledSize * 0.25, scaledSize * 0.375, scaledSize * 0.25, scaledSize * 0.625]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 1.5} />
            <Line points={[scaledSize * 0.5, scaledSize * 0.375, scaledSize * 0.5, scaledSize * 0.625]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 1.5} />
            <Line points={[scaledSize * 0.75, scaledSize * 0.375, scaledSize * 0.75, scaledSize * 0.625]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 1.5} />
            {/* Foundation blocks */}
            <Rect x={scaledSize * 0.083} y={scaledSize * 0.625} width={scaledSize * 0.17} height={scaledSize * 0.17} fill="#654321" stroke={selectionStroke || "#654321"} strokeWidth={sw * 0.5} />
            <Rect x={scaledSize * 0.75} y={scaledSize * 0.625} width={scaledSize * 0.17} height={scaledSize * 0.17} fill="#654321" stroke={selectionStroke || "#654321"} strokeWidth={sw * 0.5} />
          </Group>
        );

      case 'trench':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Transparent rect for hit area */}
            <Rect x={0} y={0} width={scaledSize} height={scaledSize} fill="transparent" />
            {/* Outer walls - empty inside */}
            <Rect x={scaledSize * 0.083} y={scaledSize * 0.375} width={scaledSize * 0.833} height={scaledSize * 0.29} fill="transparent" stroke={selectionStroke || "#654321"} strokeWidth={sw} />
            {/* Inner supports/braces */}
            <Line points={[scaledSize * 0.25, scaledSize * 0.375, scaledSize * 0.25, scaledSize * 0.665]} stroke={selectionStroke || "#654321"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.5, scaledSize * 0.375, scaledSize * 0.5, scaledSize * 0.665]} stroke={selectionStroke || "#654321"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.75, scaledSize * 0.375, scaledSize * 0.75, scaledSize * 0.665]} stroke={selectionStroke || "#654321"} strokeWidth={sw} />
          </Group>
        );

      case 'castle':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Main wall */}
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.33} width={scaledSize * 0.67} height={scaledSize * 0.54} fill="rgba(245, 245, 245, 0.7)" stroke={selectionStroke || "#DCDCDC"} strokeWidth={sw} />
            {/* Left tower */}
            <Rect x={scaledSize * 0.125} y={scaledSize * 0.17} width={scaledSize * 0.17} height={scaledSize * 0.25} fill="rgba(245, 245, 245, 0.7)" stroke={selectionStroke || "#DCDCDC"} strokeWidth={sw} />
            {/* Right tower */}
            <Rect x={scaledSize * 0.71} y={scaledSize * 0.17} width={scaledSize * 0.17} height={scaledSize * 0.25} fill="rgba(245, 245, 245, 0.7)" stroke={selectionStroke || "#DCDCDC"} strokeWidth={sw} />
            {/* Battlements left */}
            <Rect x={scaledSize * 0.125} y={scaledSize * 0.125} width={scaledSize * 0.063} height={scaledSize * 0.083} fill="rgba(245, 245, 245, 0.7)" stroke={selectionStroke || "#DCDCDC"} strokeWidth={sw * 0.5} />
            <Rect x={scaledSize * 0.23} y={scaledSize * 0.125} width={scaledSize * 0.063} height={scaledSize * 0.083} fill="rgba(245, 245, 245, 0.7)" stroke={selectionStroke || "#DCDCDC"} strokeWidth={sw * 0.5} />
            {/* Battlements right */}
            <Rect x={scaledSize * 0.71} y={scaledSize * 0.125} width={scaledSize * 0.063} height={scaledSize * 0.083} fill="rgba(245, 245, 245, 0.7)" stroke={selectionStroke || "#DCDCDC"} strokeWidth={sw * 0.5} />
            <Rect x={scaledSize * 0.813} y={scaledSize * 0.125} width={scaledSize * 0.063} height={scaledSize * 0.083} fill="rgba(245, 245, 245, 0.7)" stroke={selectionStroke || "#DCDCDC"} strokeWidth={sw * 0.5} />
            {/* Gate (arch) */}
            <Line
              points={[
                scaledSize * 0.42, scaledSize * 0.875,
                scaledSize * 0.42, scaledSize * 0.67,
                scaledSize * 0.5, scaledSize * 0.58,
                scaledSize * 0.58, scaledSize * 0.67,
                scaledSize * 0.58, scaledSize * 0.875,
              ]}
              fill="#654321"
              stroke={selectionStroke || "#654321"}
              strokeWidth={sw}
              closed
            />
          </Group>
        );

      case 'tower':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Main tower body */}
            <Rect x={scaledSize * 0.33} y={scaledSize * 0.29} width={scaledSize * 0.33} height={scaledSize * 0.625} fill="rgba(139, 90, 43, 0.7)" stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            {/* Observation platform */}
            <Rect x={scaledSize * 0.21} y={scaledSize * 0.17} width={scaledSize * 0.58} height={scaledSize * 0.17} fill="rgba(139, 90, 43, 0.8)" stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            {/* Railings */}
            <Line points={[scaledSize * 0.21, scaledSize * 0.17, scaledSize * 0.21, scaledSize * 0.083]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.375, scaledSize * 0.17, scaledSize * 0.375, scaledSize * 0.083]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.625, scaledSize * 0.17, scaledSize * 0.625, scaledSize * 0.083]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.79, scaledSize * 0.17, scaledSize * 0.79, scaledSize * 0.083]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.21, scaledSize * 0.083, scaledSize * 0.79, scaledSize * 0.083]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            {/* Ladder */}
            <Line points={[scaledSize * 0.5, scaledSize * 0.33, scaledSize * 0.5, scaledSize * 0.917]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 0.5} />
            <Line points={[scaledSize * 0.46, scaledSize * 0.42, scaledSize * 0.54, scaledSize * 0.42]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 0.5} />
            <Line points={[scaledSize * 0.46, scaledSize * 0.54, scaledSize * 0.54, scaledSize * 0.54]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 0.5} />
            <Line points={[scaledSize * 0.46, scaledSize * 0.67, scaledSize * 0.54, scaledSize * 0.67]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 0.5} />
            <Line points={[scaledSize * 0.46, scaledSize * 0.79, scaledSize * 0.54, scaledSize * 0.79]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw * 0.5} />
            {/* Base supports */}
            <Line points={[scaledSize * 0.33, scaledSize * 0.917, scaledSize * 0.21, scaledSize * 0.917]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
            <Line points={[scaledSize * 0.67, scaledSize * 0.917, scaledSize * 0.79, scaledSize * 0.917]} stroke={selectionStroke || "#8B5A2B"} strokeWidth={sw} />
          </Group>
        );

      case 'trailer':
        return (
          <Group key={element.id} {...commonGroupProps}>
            {/* Main body */}
            <Rect x={scaledSize * 0.083} y={scaledSize * 0.33} width={scaledSize * 0.75} height={scaledSize * 0.375} rx={scaledSize * 0.04} fill="rgba(220, 220, 220, 0.7)" stroke={selectionStroke || "#A9A9A9"} strokeWidth={sw} />
            {/* Front rounded part */}
            <Line
              points={[
                scaledSize * 0.833, scaledSize * 0.33,
                scaledSize * 0.917, scaledSize * 0.42,
                scaledSize * 0.917, scaledSize * 0.58,
                scaledSize * 0.833, scaledSize * 0.71,
              ]}
              fill="rgba(220, 220, 220, 0.7)"
              stroke={selectionStroke || "#A9A9A9"}
              strokeWidth={sw}
              closed={false}
            />
            {/* Windows */}
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.42} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(135, 206, 250, 0.6)" stroke="#87CEEB" strokeWidth={sw * 0.5} />
            <Rect x={scaledSize * 0.375} y={scaledSize * 0.42} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(135, 206, 250, 0.6)" stroke="#87CEEB" strokeWidth={sw * 0.5} />
            <Rect x={scaledSize * 0.58} y={scaledSize * 0.42} width={scaledSize * 0.125} height={scaledSize * 0.125} fill="rgba(135, 206, 250, 0.6)" stroke="#87CEEB" strokeWidth={sw * 0.5} />
            {/* Door */}
            <Rect x={scaledSize * 0.21} y={scaledSize * 0.58} width={scaledSize * 0.17} height={scaledSize * 0.125} fill="#888" stroke={selectionStroke || "#666"} strokeWidth={sw * 0.5} />
            {/* Wheels */}
            <Circle x={scaledSize * 0.25} y={scaledSize * 0.79} radius={scaledSize * 0.083} fill="#333" stroke="#333" strokeWidth={sw * 0.5} />
            <Circle x={scaledSize * 0.67} y={scaledSize * 0.79} radius={scaledSize * 0.083} fill="#333" stroke="#333" strokeWidth={sw * 0.5} />
            {/* Hitch */}
            <Line points={[0, scaledSize * 0.54, scaledSize * 0.083, scaledSize * 0.54]} stroke={selectionStroke || "#555"} strokeWidth={sw * 1.5} />
            <Circle x={0} y={scaledSize * 0.54} radius={scaledSize * 0.04} fill="#555" stroke="#555" strokeWidth={sw * 0.5} />
          </Group>
        );

      default:
        // Fallback to a simple rect
        return (
          <Group key={element.id} {...commonGroupProps}>
            <Rect x={scaledSize * 0.17} y={scaledSize * 0.17} width={scaledSize * 0.67} height={scaledSize * 0.67} fill={element.fill || "rgba(128, 128, 128, 0.5)"} stroke={selectionStroke || "#888"} strokeWidth={sw} />
          </Group>
        );
    }
  };

  // Render schema element
  const renderElement = (element: SchemaElement, isPreview = false) => {
    const isSelected = editorState.selectedElementIds.includes(element.id);

    const commonProps = {
      id: element.id,
      x: element.x,
      y: element.y,
      fill: element.fill,
      stroke: isSelected ? '#00ff00' : element.stroke,
      strokeWidth: element.strokeWidth || 2,
      draggable: !isReadOnly && !element.locked && mode === 'schema' && editorState.tool === 'select',
      onClick: (e: any) => handleElementClick(element.id, e),
      onTap: (e: any) => handleElementClick(element.id, e),
      onDragStart: (e: any) => handleElementDragStart(element.id, e),
      onDragMove: (e: any) => handleElementDragMove(element.id, e),
      onDragEnd: (e: any) => handleElementDragEnd(element.id, e),
      onTransformEnd: (e: any) => handleTransformEnd(element.id, e),
      rotation: element.rotation || 0,
    };

    switch (element.type) {
      case 'icon':
        // Render icon element with proper graphics
        return renderIcon(element, 1, isSelected);
      case 'rect':
        const isBorderElement = element.label === 'border';
        const rectWidth = element.width || 0;
        const rectHeight = element.height || 0;

        if (isBorderElement) {
          // Render border as 4 separate lines so clicks inside don't trigger selection
          const borderStroke = isSelected ? '#00ff00' : (element.stroke || '#ff6600');
          const borderSW = element.strokeWidth || 3;
          return (
            <Group
              key={element.id}
              id={element.id}
              x={element.x}
              y={element.y}
              draggable={!isReadOnly && !element.locked && mode === 'schema' && editorState.tool === 'select'}
              onClick={(e: any) => handleElementClick(element.id, e)}
              onTap={(e: any) => handleElementClick(element.id, e)}
              onDragStart={(e: any) => handleElementDragStart(element.id, e)}
              onDragMove={(e: any) => handleElementDragMove(element.id, e)}
              onDragEnd={(e: any) => handleElementDragEnd(element.id, e)}
              onTransformEnd={(e: any) => handleTransformEnd(element.id, e)}
              rotation={element.rotation || 0}
            >
              {/* Top */}
              <Line points={[0, 0, rectWidth, 0]} stroke={borderStroke} strokeWidth={borderSW} dash={[10, 5]} hitStrokeWidth={12} />
              {/* Right */}
              <Line points={[rectWidth, 0, rectWidth, rectHeight]} stroke={borderStroke} strokeWidth={borderSW} dash={[10, 5]} hitStrokeWidth={12} />
              {/* Bottom */}
              <Line points={[rectWidth, rectHeight, 0, rectHeight]} stroke={borderStroke} strokeWidth={borderSW} dash={[10, 5]} hitStrokeWidth={12} />
              {/* Left */}
              <Line points={[0, rectHeight, 0, 0]} stroke={borderStroke} strokeWidth={borderSW} dash={[10, 5]} hitStrokeWidth={12} />
            </Group>
          );
        }

        // If element has a label (like SAFE ZONE, DANGER), render with text
        if (element.label && element.label !== 'border') {
          return (
            <Group
              key={element.id}
              id={element.id}
              x={element.x}
              y={element.y}
              draggable={!isReadOnly && !element.locked && mode === 'schema' && editorState.tool === 'select'}
              onClick={(e: any) => handleElementClick(element.id, e)}
              onTap={(e: any) => handleElementClick(element.id, e)}
              onDragStart={(e: any) => handleElementDragStart(element.id, e)}
              onDragMove={(e: any) => handleElementDragMove(element.id, e)}
              onDragEnd={(e: any) => handleElementDragEnd(element.id, e)}
              onTransformEnd={(e: any) => handleTransformEnd(element.id, e)}
              rotation={element.rotation || 0}
            >
              <Rect
                width={rectWidth}
                height={rectHeight}
                fill={element.fill}
                stroke={isSelected ? '#00ff00' : element.stroke}
                strokeWidth={element.strokeWidth || 1}
              />
              <Text
                x={0}
                y={rectHeight / 2 - 6}
                width={rectWidth}
                text={element.label}
                fontSize={12}
                fontFamily="Arial"
                fontStyle="bold"
                fill={element.stroke || '#fff'}
                align="center"
              />
            </Group>
          );
        }

        return (
          <Rect
            key={element.id}
            {...commonProps}
            width={rectWidth}
            height={rectHeight}
          />
        );
      case 'circle':
        return (
          <Circle
            key={element.id}
            {...commonProps}
            radius={element.radius || 0}
          />
        );
      case 'line':
      case 'freehand':
        return (
          <Line
            key={element.id}
            {...commonProps}
            points={element.points || []}
            lineCap="round"
            lineJoin="round"
          />
        );
      case 'road':
        return (
          <Line
            key={element.id}
            {...commonProps}
            points={element.points || []}
            lineCap="round"
            lineJoin="round"
            tension={0.5}  // Makes the line curved/smooth
          />
        );
      case 'polygon':
        return (
          <Line
            key={element.id}
            {...commonProps}
            x={0}
            y={0}
            points={element.points || []}
            closed
            lineCap="round"
            lineJoin="round"
          />
        );
      case 'text':
        const textScale = element.width || 1;
        return (
          <Text
            key={element.id}
            {...commonProps}
            text={element.text || ''}
            fontSize={16 * textScale}
            fontFamily="Arial"
          />
        );
      default:
        return null;
    }
  };

  // Render polygon preview while drawing
  const renderPolygonPreview = () => {
    if (!isDrawingPolygon || polygonPoints.length < 2) return null;

    return (
      <Line
        points={polygonPoints}
        stroke={editorState.strokeColor}
        strokeWidth={editorState.strokeWidth}
        fill={editorState.fillColor}
        closed={false}
        dash={[10, 5]}
      />
    );
  };

  // Render node on canvas
  const renderNode = (node: NodeWithPosition) => {
    if (node.positionX === null || node.positionY === null) return null;

    const nodeScale = 1.4; // Fixed node scale
    const size = 40 * nodeScale;
    const isMainNode = node.nodeType === 'MapMainNode';
    const color = getDeviceColor(node.nodeType);

    return (
      <Group
        key={node.id}
        x={node.positionX}
        y={node.positionY}
        draggable={!isReadOnly && !node.locked && mode === 'nodes'}
        onDragEnd={(e) => handleNodeDragEnd(node, e)}
      >
        {isMainNode ? (
          // Star for main node
          <Line
            points={[
              0, -size/2,
              size/6, -size/6,
              size/2, -size/6,
              size/4, size/8,
              size/3, size/2,
              0, size/4,
              -size/3, size/2,
              -size/4, size/8,
              -size/2, -size/6,
              -size/6, -size/6,
            ]}
            fill={color}
            stroke="#000"
            strokeWidth={1}
            closed
          />
        ) : (
          // Circle for regular node
          <Circle
            radius={size/2}
            fill={color}
            stroke="#000"
            strokeWidth={2}
          />
        )}
        <Text
          text={node.nodeCode}
          fontSize={14}
          fill="#fff"
          fontStyle="bold"
          align="center"
          verticalAlign="middle"
          offsetX={20}
          offsetY={7}
          width={40}
          shadowColor="#000"
          shadowBlur={2}
          shadowOffsetX={1}
          shadowOffsetY={1}
        />
        {node.locked && (
          <Text
            text="🔒"
            fontSize={10}
            x={size/2 - 8}
            y={-size/2}
          />
        )}
      </Group>
    );
  };

  // Render placed device on canvas
  const renderDevice = (device: TemplateDevicePosition, index: number) => {
    // Не рендерим устройства без позиции
    if (!device.positionX && !device.positionY) return null;

    const deviceScale = device.scale || 1;
    const size = 48 * deviceScale;  // Увеличенный размер для иконок
    const color = getDeviceColor(device.deviceType);
    const iconImage = deviceIconImages[device.fmdId];
    const hasIcon = !!iconImage;

    return (
      <Group
        key={device.fmdId}
        x={device.positionX}
        y={device.positionY}
        draggable={!isReadOnly && !device.locked && mode === 'devices'}
        onDragEnd={(e) => {
          const newX = e.target.x();
          const newY = e.target.y();
          setDevicePositions(prev =>
            prev.map(d =>
              d.fmdId === device.fmdId
                ? { ...d, positionX: newX, positionY: newY }
                : d
            )
          );
        }}
      >
        {hasIcon ? (
          // Показываем иконку устройства
          <>
            <KonvaImage
              image={iconImage}
              width={size}
              height={size}
              offsetX={size / 2}
              offsetY={size / 2}
            />
            {/* Номер устройства в углу */}
            <Circle
              x={size / 2 - 8}
              y={-size / 2 + 8}
              radius={10}
              fill={color}
              stroke="#000"
              strokeWidth={1}
            />
            <Text
              text={String(device.displayNumber || index + 1)}
              fontSize={10}
              fill="#fff"
              fontStyle="bold"
              x={size / 2 - 13}
              y={-size / 2 + 2}
              width={10}
            />
          </>
        ) : (
          // Дефолтная иконка: круг с цветом + название устройства
          <>
            <Circle
              radius={size / 2}
              fill={color}
              stroke="#000"
              strokeWidth={2}
            />
            <Text
              text={String(device.displayNumber || index + 1)}
              fontSize={16}
              fill="#fff"
              fontStyle="bold"
              align="center"
              verticalAlign="middle"
              offsetX={8}
              offsetY={8}
              width={16}
            />
            {/* Подпись с названием устройства под иконкой */}
            <Rect
              x={-60}
              y={size / 2 + 4}
              width={120}
              height={20}
              fill="rgba(0,0,0,0.7)"
              cornerRadius={3}
            />
            <Text
              text={device.deviceType}
              fontSize={14}
              fill="#fff"
              x={-60}
              y={size / 2 + 6}
              width={120}
              align="center"
              fontStyle="bold"
            />
          </>
        )}
        {device.locked && (
          <Text
            text="🔒"
            fontSize={12}
            x={hasIcon ? size / 2 - 16 : size / 2 - 8}
            y={hasIcon ? size / 2 - 16 : -size / 2}
          />
        )}
      </Group>
    );
  };

  const getCursor = () => {
    // Show grabbing cursor when actively panning
    if (isPanning) {
      return 'grabbing';
    }

    // Show grab cursor when space is pressed (ready to pan)
    if (isSpacePressed) {
      return 'grab';
    }

    // Show crosshair when placing template, node, or device
    if (selectedTemplate || nodeToPlace || selectedDeviceForPlacement) {
      return 'crosshair';
    }

    switch (editorState.tool) {
      case 'rect':
      case 'circle':
      case 'line':
      case 'freehand':
      case 'polygon':
      case 'border':
        return 'crosshair';
      case 'text':
        return 'text';
      case 'eraser':
        return 'not-allowed';
      case 'pan':
        return 'grab';
      default:
        return 'default';
    }
  };

  return (
    <EditorContainer $isFullscreen={false}>
      {/* Show hint when template is selected */}
      {selectedTemplate && (
        <PlacementHint>
          Click on canvas to place "{selectedTemplate.label}" (Esc - cancel)
        </PlacementHint>
      )}

      {/* Show hint when device is selected for placement */}
      {selectedDeviceForPlacement && (
        <PlacementHint>
          Click on canvas to place "{selectedDeviceForPlacement.deviceName}" (Esc - cancel)
        </PlacementHint>
      )}

      <EditorToolbar
        editorState={editorState}
        mode={mode}
        isReadOnly={isReadOnly}
        onToolChange={handleToolChange}
        onZoomChange={handleZoomChange}
        onResetView={handleResetView}
        onSave={handleSave}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onExport={handleExport}
        onColorChange={(fill, stroke) => setEditorState(prev => ({ ...prev, fillColor: fill, strokeColor: stroke }))}
        onStrokeWidthChange={(width) => setEditorState(prev => ({ ...prev, strokeWidth: width }))}
        canUndo={history.past.length > 0}
        canRedo={history.future.length > 0}
        selectedCount={editorState.selectedElementIds.length}
        onDeleteSelected={handleDeleteSelected}
        onCopy={handleCopy}
        onPaste={handlePaste}
        hasClipboard={editorState.clipboard.length > 0}
        canvasBgColor={canvasBgColor}
        onToggleCanvasBg={() => setCanvasBgColor(prev => prev === 'dark' ? 'white' : 'dark')}
      />

      <MainContent>
        <CanvasContainer ref={containerRef}>
        <StageWrapper $cursor={getCursor()}>
          <Stage
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            scaleX={editorState.zoom}
            scaleY={editorState.zoom}
            x={stagePosition.x}
            y={stagePosition.y}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDblClick={handleDoubleClick}
            onDblTap={handleDoubleClick}
          >
            {/* Background layer */}
            <Layer>
              {/* Canvas border/shadow */}
              <Rect
                x={-2}
                y={-2}
                width={CANVAS_WIDTH + 4}
                height={CANVAS_HEIGHT + 4}
                fill="transparent"
                stroke="#333"
                strokeWidth={2}
                shadowColor="#000"
                shadowBlur={20}
                shadowOpacity={0.8}
              />
              {/* Canvas background */}
              <Rect
                name="background"
                x={0}
                y={0}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                fill={canvasBgColor === 'white' ? '#ffffff' : CANVAS_BACKGROUND}
              />
              {backgroundImage && (
                <KonvaImage
                  name="background"
                  {...getBackgroundImageProps()}
                />
              )}
            </Layer>

            {/* Schema elements layer */}
            <Layer>
              {elements.map(el => renderElement(el))}
              {currentElement && renderElement(currentElement, true)}
              {renderPolygonPreview()}

              {/* Transformer for selected elements */}
              {mode === 'schema' && editorState.tool === 'select' && (
                <Transformer
                  ref={transformerRef}
                  rotateEnabled={true}
                  rotateAnchorOffset={40}
                  rotateAnchorSize={12}
                  rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                  anchorSize={10}
                  anchorCornerRadius={2}
                  padding={5}
                  ignoreStroke={true}
                  boundBoxFunc={(oldBox, newBox) => {
                    // Only limit minimum size, allow any maximum
                    if (newBox.width < 10 || newBox.height < 10) {
                      return oldBox;
                    }
                    // Allow elements to be as large as needed (no max limit)
                    return newBox;
                  }}
                />
              )}
            </Layer>

            {/* Nodes layer */}
            {(mode === 'nodes' || mode === 'schema') && (
              <Layer>
                {nodes.map(renderNode)}
              </Layer>
            )}

            {/* Devices layer */}
            {mode === 'devices' && (
              <Layer>
                {devicePositions.map((device, index) => renderDevice(device, index))}
              </Layer>
            )}
          </Stage>
        </StageWrapper>
        </CanvasContainer>

        {/* Template library - only in schema mode and not read-only */}
        {mode === 'schema' && !isReadOnly && (
          <TemplateLibrary
            onSelectTemplate={handleTemplateSelect}
            selectedTemplateId={selectedTemplateId}
            isCollapsed={isLibraryCollapsed}
            onToggleCollapse={() => setIsLibraryCollapsed(prev => !prev)}
          />
        )}

        {/* Node position manager - only in nodes mode and not read-only */}
        {mode === 'nodes' && !isReadOnly && (
          <NodePositionManager
            nodes={nodes}
            selectedNodeId={selectedNodeId}
            onSelectNode={setSelectedNodeId}
            onUpdatePosition={handleNodePositionUpdateFromManager}
            onPlaceOnCanvas={handlePlaceNodeOnCanvas}
            isCollapsed={isNodeManagerCollapsed}
            onToggleCollapse={() => setIsNodeManagerCollapsed(prev => !prev)}
          />
        )}

        {/* Devices panel - only in devices mode and not read-only */}
        {mode === 'devices' && !isReadOnly && (
          <DevicesPanel
            fieldId={mapData.fieldId || ''}
            mapId={mapId}
            templates={operationTemplates}
            selectedTemplateId={selectedOperationTemplateId}
            onSelectTemplate={setSelectedOperationTemplateId}
            onSaveTemplate={handleSaveOperationTemplate}
            onDeleteTemplate={handleDeleteOperationTemplate}
            onDeviceSelect={handleDeviceSelect}
            selectedDeviceIndex={selectedDeviceIndex}
            devicePositions={devicePositions}
            onDevicePositionsChange={setDevicePositions}
            onShowSuccess={onShowSuccess}
            isCollapsed={isDevicesPanelCollapsed}
            onToggleCollapse={() => setIsDevicesPanelCollapsed(prev => !prev)}
          />
        )}
      </MainContent>

      {/* Text input dialog */}
      {showTextInput && (
        <TextInputOverlay onClick={() => { setShowTextInput(false); setEditingTextId(null); }}>
          <TextInputDialog onClick={e => e.stopPropagation()}>
            <h3>{editingTextId ? 'Edit Text' : 'Add Text'}</h3>
            <input
              type="text"
              value={textInputValue}
              onChange={e => setTextInputValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleTextSubmit();
                if (e.key === 'Escape') { setShowTextInput(false); setEditingTextId(null); }
              }}
              autoFocus
              placeholder="Enter text..."
            />
            <div className="buttons">
              <button className="secondary" onClick={() => { setShowTextInput(false); setEditingTextId(null); }}>
                Cancel
              </button>
              <button className="primary" onClick={handleTextSubmit}>
                {editingTextId ? 'Save' : 'Add'}
              </button>
            </div>
          </TextInputDialog>
        </TextInputOverlay>
      )}
    </EditorContainer>
  );
};

export default MapSchemaEditor;
